import Vuex from 'vuex'
import Vue from 'vue'
import contract from './contract'
import invoice from './invoice'

Vue.use(Vuex)

export default new Vuex.Store({
    strict: false,
    modules: {
        contract,
        invoice
    },
    state: {
        // Base
        version: '1.75',
        authToken: null,
        initialized: false,
        auth_redirected: false,
        user: {},
        env: null,
        searchQueries: {},
        dataBelongsTo: 'all',
        dateRange: [],
        boxView: 'list',
        exporting: false,
        hideSensitiveData: true,
        showActive: true,
        showCancelledOrders: false,
        showPaidOrders: true,
        connect: {
            version: '1.20',
            date: 'Juni 2025',
            download: '/files/PlanitEasy-Connect-Setup-1.2.0.exe'
        },
        lastOpenedFloorplan: 0,
        selectedFilters: null,
        organisations: null,
        // Token refresh state
        isRefreshingToken: false,
        lastTokenRefresh: null,
    },

    actions: {
        setToken({commit, dispatch, state}, payload){
            console.log('[🔥] Setting authentication token!')

            return axios.post('/setauthtoken', {'token': payload}).then(() => {
                commit('setToken', payload)
                // Save token to localStorage for persistence
                localStorage.setItem('auth_token', payload)
            })
        },

        checkSavedToken({commit, dispatch, state}) {
            const savedToken = localStorage.getItem('auth_token')
            const savedUser = localStorage.getItem('user_data')

            if (savedToken) {
                console.log('[🔥] Found saved authentication token')
                commit('setToken', savedToken)

                // If we have saved user data, load it immediately
                if (savedUser) {
                    try {
                        const userData = JSON.parse(savedUser)

                        // Parse user settings if they exist (same logic as in getUser action)
                        if (userData.settings) {
                            userData.settings = JSON.parse(userData.settings)
                        }

                        commit('setUser', userData)
                        commit('setInitialized', true)

                        if (userData.organisation) {
                            dispatch('setDataBelongsTo', userData.organisation)
                        }

                        // Always reset dateRange to null on app load
                        dispatch('setDateRange', null)

                        // Set organisations if available
                        const savedOrgs = localStorage.getItem('organisations')
                        if (savedOrgs) {
                            try {
                                commit('setOrganisations', JSON.parse(savedOrgs))
                            } catch (e) {
                                console.error('[❌] Error parsing saved organisations:', e)
                            }
                        }

                        // Verify token is still valid by trying to refresh user data
                        return dispatch('refreshUserData')
                            .then(success => {
                                if (success) {
                                    return true
                                } else {
                                    // If refresh failed, token might be invalid
                                    commit('clearUserData')
                                    return false
                                }
                            })
                    } catch (e) {
                        console.error('[❌] Error parsing saved user data:', e)
                    }
                }

                // If no saved user data or parsing failed, load app normally
                return dispatch('loadApp')
                    .then(() => true)
                    .catch(() => {
                        // If token is invalid, remove all saved data
                        commit('clearUserData')
                        return false
                    })
            }
            return Promise.resolve(false)
        },

        refreshUserData({commit, state}) {
            // Refresh user data in the background without blocking UI
            console.log('[🔥] Refreshing user data in background')
            return axios.get('/api/user', {
                headers: {'Authorization': 'bearer ' + state.authToken}
            }).then((response) => {
                // Parse user settings if they exist (same logic as in getUser action)
                if (response.data.user.settings) {
                    response.data.user.settings = JSON.parse(response.data.user.settings)
                }

                commit('setUser', response.data.user)

                if(_.has(response.data, 'organisations')){
                    commit('setOrganisations', response.data.organisations)
                    // Save organisations to localStorage
                    localStorage.setItem('organisations', JSON.stringify(response.data.organisations))
                }

                // Save user data to localStorage (now with parsed settings)
                localStorage.setItem('user_data', JSON.stringify(response.data.user))

                return true
            }).catch((error) => {
                console.error('[❌] Error refreshing user data:', error)

                // Check if error is due to unauthorized (expired token)
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    console.log('[❌] Token expired or unauthorized, clearing user data')

                    // Check if the page was just loaded (within the last 3 seconds)
                    // Using modern Performance API instead of deprecated window.performance.timing
                    const pageLoadTime = window.performance &&
                        window.performance.getEntriesByType &&
                        window.performance.getEntriesByType('navigation').length > 0 ?
                        window.performance.getEntriesByType('navigation')[0].startTime : 0;
                    const pageJustLoaded = pageLoadTime !== undefined &&
                        (Date.now() - (performance.timeOrigin + pageLoadTime) < 3000);

                    // If page just loaded, try to refresh the token instead of immediate logout
                    if (pageJustLoaded && typeof window !== 'undefined') {
                        console.log('[🔄] Page just loaded, attempting to refresh token...')

                        // Don't clear user data yet, let the refresh attempt handle it
                        return dispatch('attemptTokenRefresh')
                            .catch(() => {
                                // If refresh fails, then clear user data and redirect
                                commit('clearUserData')
                                window.location = '/logout/force?expired=true'
                                return false
                            })
                    } else {
                        // Normal flow - clear data and redirect
                        commit('clearUserData')

                        // Redirect to login page if we're in the browser context
                        if (typeof window !== 'undefined') {
                            window.location = '/logout/force?expired=true'
                        }
                    }
                }

                return false
            })
        },

        loadApp({commit, dispatch, state}){
            return new Promise((resolve) => {
                /**
                 * Get the authenticated user
                 */
                let getUser = this.dispatch('getUser')

                // Set daterange to this month.
                dispatch('setDateRange', null)

                /**
                 * All finished
                 */
                Promise.all([getUser]).then(() => {
                    setTimeout(() => {
                        commit('setInitialized', true)
                    }, 500)

                    resolve('loaded')
                }).catch((error) => {
                    console.error('[❌] Error while loading app:', error)
                })
            })
        },

        getUser({commit, dispatch, state}){
            return new Promise((resolve, reject) => {
                axios.get('/api/user', {
                    headers: {'Authorization': 'bearer ' + state.authToken}
                }).then((response) => {
                    // Save user data to localStorage
                    localStorage.setItem('user_data', JSON.stringify(response.data.user))

                    if(_.has(response.data, 'organisations')){
                        // console.log('[🔥] Setting organisations:', response.data.organisations)
                        commit('setOrganisations', response.data.organisations)

                        // Save organisations to localStorage
                        localStorage.setItem('organisations', JSON.stringify(response.data.organisations))
                    }

                    dispatch('setDataBelongsTo', response.data.user.organisation)

                    // Parse user settings if they exist
                    if (response.data.user.settings) {
                        response.data.user.settings = JSON.parse(response.data.user.settings)
                    }

                    commit('setUser', response.data.user)

                    resolve(true)
                }).catch((error) => {
                    console.error('[❌] Error while fetching user:', error)
                    reject(error)
                })
            })
        },

        switchOrganisation({commit, dispatch, state}, id){
            return new Promise((resolve) => {
                axios.post('/api/user/switchorganisation', {
                    id
                }, {
                    headers: {'Authorization': 'bearer ' + state.authToken}
                }).then(() => {
                    commit('emptySearchQueries') // Empty search queries

                    dispatch('loadApp').then(() => {
                        resolve()
                    })
                }).catch((error) => {
                    console.error('[❌] Error while switching organisation:', error)
                })
            })
        },

        saveUserSettings({commit}, settings) {
            commit('setUserSettings', settings)
        },

        fetchConfirmStops({commit, state}) {
            axios.get('/api/contract/fetchconfirmstops', {
                headers: {'Authorization': 'bearer ' + state.authToken}
            }).then((response) => {
                commit('setConfirmStops', response.data)
            })
        },

        fetchInvoicesNeedsAction({commit, state}) {
            axios.get('/api/invoices/needs-action', {
                headers: {'Authorization': 'bearer ' + state.authToken}
            }).then((response) => {
                commit('setInvoicesNeedsAction', response.data)
            })
        },

        setSearchQuery({commit}, payload){
            if(!_.isNull(payload.query) && !_.isNull(payload.type)){
                console.log('[🔥] Set search query for type: '+payload.type, payload.query)
            }

            if(!_.isNull(payload.type)){
                commit('setSearchQuery', payload)
            }
        },

        setDataBelongsTo({commit}, payload){
            commit('setDataBelongsTo', payload)
        },

        setDateRange({commit}, payload){
            // Ensure we don't store invalid date ranges
            if (payload && Array.isArray(payload) && payload.length > 0) {
                commit('setDateRange', payload)
            } else {
                // If null or empty, explicitly set to null
                commit('setDateRange', null)
            }
        },

        setBoxView({commit}, payload){
            commit('setBoxView', payload)
        },

        toggleSensitiveData({commit, dispatch, state}, payload){
            // If payload is set, use that value. Otherwise toggle the current value.
            commit('setSensitiveData', payload !== undefined ? payload : !state.hideSensitiveData)
        },

        // setupConnect() {
        //     window.Echo = new Echo({
        //         broadcaster: 'pusher',
        //         key: process.env.MIX_PUSHER_APP_KEY,
        //         // cluster: process.env.MIX_PUSHER_APP_CLUSTER,
        //         encrypted: false,
        //         wsHost: window.location.hostname,
        //         wsPort: 6001,
        //         wssPort: 6001,
        //         enabledTransports: ['ws', 'flash']
        //     })
        // },

        updateConnectVisibility({commit}, payload){
            commit('setConnect', payload)
        },

        setShowActive({commit}, payload) {
            commit('setShowActive', payload)
        },

        setAuthRedirected({commit}, value) {
            commit('setAuthRedirected', value)
        },

        setShowCancelledOrders({commit}, value) {
            commit('setShowCancelledOrders', value)
        },
        setShowPaidOrders({commit}, value) {
            commit('setShowPaidOrders', value)
        },

        setLastOpenedFloorplan({commit, state}, value) {
            commit('setLastOpenedFloorplan', value)

            // If the open_preference is 4, set the last_opened_floorplan to the value if not already set.
            if(_.has(state.user, 'settings.open_preference') && state.user.settings.open_preference == 4){
                axios.post('/api/user/save',  { settings: { ...state.user.settings, last_opened_floorplan: value }}, {
                    headers: {'Authorization': 'bearer ' + state.authToken}
                }).then(() => {
                    commit('setUserSettings', { ...state.user.settings, last_opened_floorplan: value })
                })
            }
        },

        setSelectedFilters({commit}, payload) {
            commit('setSelectedFilters', payload)
        },

        /**
         * Attempt to refresh the token when it has expired
         * This is used when the user returns to the app after being away
         * @param {boolean} silent - If true, don't show any toasts
         */
        attemptTokenRefresh({commit, state}, silent = false) {
            return new Promise((resolve, reject) => {
                // Check if we're already refreshing the token
                if (state.isRefreshingToken) {
                    console.log('[🔄] Token refresh already in progress, skipping duplicate request')
                    return resolve(false)
                }

                // Check if we've refreshed the token recently (within the last 30 seconds)
                // This prevents multiple components from triggering refresh in quick succession
                // and helps avoid token blacklisting issues
                const MIN_REFRESH_INTERVAL = 30000 // 30 seconds
                if (state.lastTokenRefresh && (Date.now() - state.lastTokenRefresh < MIN_REFRESH_INTERVAL)) {
                    console.log('[🔄] Token was refreshed recently, skipping duplicate request')
                    return resolve(true)
                }

                // Set refreshing flag to true
                commit('setIsRefreshingToken', true)

                console.log('[🔄] Attempting to refresh token...')

                // Show loading toast to indicate we're trying to refresh the session (unless silent mode)
                let loadingToast
                if (!silent && Vue.prototype.$buefy && Vue.prototype.$buefy.toast) {
                    loadingToast = Vue.prototype.$buefy.toast.open({
                        message: 'Je sessie wordt vernieuwd...',
                        type: 'is-info',
                        position: 'is-top',
                        indefinite: true,
                        queue: false
                    })
                }

                // Get the current token from localStorage
                const token = localStorage.getItem('auth_token')

                if (!token) {
                    console.error('[❌] No token available for refresh')
                    if (loadingToast) loadingToast.close()
                    commit('setIsRefreshingToken', false)
                    reject(new Error('No token available'))
                    return
                }

                // Try to refresh the token
                axios.post('/api/refresh-token', { token: token })
                    .then(response => {
                        if (response.data.token) {
                            console.log('[✅] Token refreshed successfully')

                            // Update token in localStorage
                            localStorage.setItem('auth_token', response.data.token)

                            // Update token in store
                            commit('setToken', response.data.token)

                            // Update last refresh timestamp
                            commit('setLastTokenRefresh')

                            // Close loading toast and show success
                            if (loadingToast) loadingToast.close()
                            resolve(true)
                        } else {
                            console.error('[❌] No token received from refresh endpoint')
                            if (loadingToast) loadingToast.close()
                            commit('setIsRefreshingToken', false)
                            reject(new Error('No token received'))
                        }
                    })
                    .catch(error => {
                        console.error('[❌] Error refreshing token:', error)

                        // Close loading toast and show error
                        if (loadingToast) loadingToast.close()
                        if (Vue.prototype.$buefy && Vue.prototype.$buefy.toast) {
                            Vue.prototype.$buefy.toast.open({
                                message: 'Je bent automatisch uitgelogd wegens inactiviteit.',
                                type: 'is-danger',
                                position: 'is-top',
                                duration: 5000,
                                queue: false
                            })

                            commit('clearUserData')

                            setTimeout(() => {
                                window.location = '/logout/force?expired=true'
                            }, 2500)
                        }

                        commit('setIsRefreshingToken', false)
                        reject(error)
                    })
                    .finally(() => {
                        // Always reset the refreshing flag when done
                        commit('setIsRefreshingToken', false)
                    })
            })
        }
    },

    mutations: {
        setToken(state, payload) {
            state.authToken = payload
        },

        clearUserData(state) {
            state.authToken = null
            state.user = {}
            state.organisations = null
            state.dateRange = null
            localStorage.removeItem('auth_token')
            localStorage.removeItem('user_data')
            localStorage.removeItem('organisations')
        },

        setInitialized(state, payload){
            state.initialized = payload
        },

        setUser(state, payload){
            state.user = payload
        },

        setUserSettings(state, payload){
            state.user.settings = payload
        },

        setConfirmStops(state, payload) {
            state.user.confirm_stops = payload
        },

        setInvoicesNeedsAction(state, payload) {
            state.user.invoice_needs_action = payload
        },

        setAuthRedirected(state, payload) {
            state.auth_redirected = payload
        },

        setOrganisations(state, payload) {
            state.organisations = payload
        },

        emptySearchQueries(state) {
            state.searchQueries = {}
        },

        setSearchQuery(state, payload){
            state.searchQueries[payload.type] = payload.query
        },

        setEnvironment(state, payload){
            state.env = payload

            if(payload == 'demo'){
                state.hideSensitiveData = false
            }
        },

        startExport(state, payload){
            state.exporting = payload
        },

        setDataBelongsTo(state, payload){
            state.dataBelongsTo = payload
        },

        setDateRange(state, payload){
            state.dateRange = payload
        },

        setBoxView(state, payload){
            state.boxView = payload
        },

        setShowActive(state, payload){
            state.showActive = payload
        },

        setSensitiveData(state, payload){
            state.hideSensitiveData = payload
        },

        setConnect(state, payload){
            state.user.organisation_connect = payload
        },

        setShowCancelledOrders(state, payload){
            state.showCancelledOrders = payload
        },

        setShowPaidOrders(state, payload){
            state.showPaidOrders = payload
        },

        setLastOpenedFloorplan(state, payload){
            state.lastOpenedFloorplan = payload
        },

        setSelectedFilters(state, payload) {
            state.selectedFilters = payload
        },

        setIsRefreshingToken(state, payload) {
            state.isRefreshingToken = payload
        },

        setLastTokenRefresh(state) {
            state.lastTokenRefresh = Date.now()
        }
    }
})
